import React, { useCallback } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { UserLoggedInNavigatorScreenProps } from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { WAWidgetSizeToOverviewSize } from "../../WidgetAdd/utils/WAWidgetSizeToOverviewSize";
import { WidgetSizeT } from "../../localTypes/WidgetSizeT";
import { WASizePreview } from "../../WidgetAdd/WASizePreview/WASizePreview";
import { WidgetIntegrationPushAppy } from "widgets";
import { WAPushAppyQuery } from "./__generated__/WAPushAppyQuery.graphql";
import { WAPushAppyMutation } from "./__generated__/WAPushAppyMutation.graphql";
import { overviewWidgetSizes } from "../../WidgetAdd/utils/widgetSizes/overviewWidgetSizes";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { View } from "react-native";
import { WAFakeHeader } from "../../WidgetAdd/WAFakeHeader";
import { useSizeClass } from "../../../../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";

type Props = UserLoggedInNavigatorScreenProps<"WidgetAddPushAppy">;

export const WAPushAppy = withWebModal(WAPushAppyC, true);
function WAPushAppyC(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => <InnerWAPushAppy {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerWAPushAppy(props: Props & { fetchKey: string }) {
  const data = useLazyLoadQuery<WAPushAppyQuery>(
    graphql`
      query WAPushAppyQuery(
        $input: PreviewWidgetPushAppyIntegrationInput!
        $storecastId: ID!
      ) {
        dashboard(id: $storecastId) @required(action: THROW) {
          previewWidgetPushAppyIntegration(input: $input) {
            ...WidgetIntegrationPushAppy_widgetIntegrationPushAppy
          }
        }
      }
    `,
    {
      storecastId: props.route.params.storecastId,
      input: {
        size: "TWO_X_FOUR",
      },
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const [createWidgetIpool, loading] = useMutation<WAPushAppyMutation>(graphql`
    mutation WAPushAppyMutation(
      $storecastId: ID!
      $input: CreateWidgetIntegrationPushAppyInput!
    ) {
      dashboard(dashboardId: $storecastId) {
        createWidgetIntegrationPushAppy(widget: $input) {
          id
        }
      }
    }
  `);

  const onCreate = useCallback(
    (size: WidgetSizeT) => {
      createWidgetIpool({
        variables: {
          storecastId: props.route.params.storecastId,
          input: {
            positionX:
              props.route.params.position.x + props.route.params.position.c * 2,
            positionY: props.route.params.position.y,
            size: WAWidgetSizeToOverviewSize(size),
          },
        },
        onCompleted: () => props.navigation.pop(2),
      });
    },
    [
      createWidgetIpool,
      props.navigation,
      props.route.params.position.c,
      props.route.params.position.x,
      props.route.params.position.y,
      props.route.params.storecastId,
    ]
  );

  const isCompact = useSizeClass().width === SizeClass.COMPACT;

  return (
    <View style={{ flex: 1 }}>
      {isCompact ? null : <WAFakeHeader />}
      <WASizePreview
        fullScreen
        loading={loading}
        maxSize={props.route.params.maxSize}
        onCreate={onCreate}
        unitSize={props.route.params.unitSize}
        widgetSizes={overviewWidgetSizes}
      >
        {data.dashboard.previewWidgetPushAppyIntegration ? (
          <WidgetIntegrationPushAppy
            widget={data.dashboard.previewWidgetPushAppyIntegration}
            storecastId={props.route.params.storecastId}
            interactive={false}
            mode="IDLE"
          />
        ) : null}
      </WASizePreview>
    </View>
  );
}
